import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './DailyBiometrics.css';

const StarIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" strokeWidth="2">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
  </svg>
);

function getDateStr(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
}

const DailyBiometrics: React.FC = () => {
  const [dateOffset, setDateOffset] = useState(0); // 0=today, 1=yesterday
  const [weightKg, setWeightKg] = useState<string>('');
  const [restingHr, setRestingHr] = useState<string>('');
  const [hrv, setHrv] = useState<string>('');
  const [sleepHours, setSleepHours] = useState<string>('');
  const [sleepQuality, setSleepQuality] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchBiometrics();
  }, []);

  const fetchBiometrics = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/ai-coach/biometrics`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.weight_kg) setWeightKg(String(data.weight_kg));
        if (data.resting_hr) setRestingHr(String(data.resting_hr));
        if (data.hrv) setHrv(String(data.hrv));
        if (data.sleep_hours) setSleepHours(String(data.sleep_hours));
        if (data.sleep_quality) setSleepQuality(Number(data.sleep_quality));
      }
    } catch (e) {
      console.error('Failed to fetch biometrics:', e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const body: Record<string, any> = { date: getDateStr(dateOffset) };
      if (weightKg) body.weight_kg = parseFloat(weightKg);
      if (restingHr) body.resting_hr = parseInt(restingHr);
      if (hrv) body.hrv = parseInt(hrv);
      if (sleepHours) body.sleep_hours = parseFloat(sleepHours);
      if (sleepQuality !== null) body.sleep_quality = sleepQuality;

      const res = await fetch(`${API_BASE_URL}/api/ai-coach/biometrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      }
    } catch (e) {
      console.error('Failed to save biometrics:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="biometrics-section">
      <h3>Daily Check-in</h3>
      <p className="biometrics-subtitle">Track weight, heart rate, sleep for better coaching</p>

      <div className="biometrics-date-toggle">
        <button
          className={dateOffset === 0 ? 'active' : ''}
          onClick={() => setDateOffset(0)}
          type="button"
        >
          Today
        </button>
        <button
          className={dateOffset === 1 ? 'active' : ''}
          onClick={() => setDateOffset(1)}
          type="button"
        >
          Yesterday
        </button>
      </div>

      <div className="biometrics-form">
        <div className="biometrics-field">
          <label htmlFor="bio-weight">Weight (kg)</label>
          <input
            id="bio-weight"
            type="number"
            step="0.1"
            min="30"
            max="200"
            value={weightKg}
            onChange={e => setWeightKg(e.target.value)}
            placeholder="75.0"
          />
        </div>
        <div className="biometrics-field">
          <label htmlFor="bio-rhr">Resting HR (bpm)</label>
          <input
            id="bio-rhr"
            type="number"
            min="30"
            max="120"
            value={restingHr}
            onChange={e => setRestingHr(e.target.value)}
            placeholder="55"
          />
        </div>
        <div className="biometrics-field">
          <label htmlFor="bio-hrv">HRV (ms)</label>
          <input
            id="bio-hrv"
            type="number"
            min="1"
            max="200"
            value={hrv}
            onChange={e => setHrv(e.target.value)}
            placeholder="45"
          />
        </div>
        <div className="biometrics-field">
          <label htmlFor="bio-sleep">Sleep (hours)</label>
          <input
            id="bio-sleep"
            type="number"
            step="0.5"
            min="0"
            max="16"
            value={sleepHours}
            onChange={e => setSleepHours(e.target.value)}
            placeholder="7.5"
          />
        </div>
        <div className="biometrics-field">
          <label>Sleep Quality</label>
          <div className="star-rating-row">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                className={`star-btn ${sleepQuality !== null && star <= sleepQuality ? 'filled' : ''}`}
                onClick={() => setSleepQuality(sleepQuality === star ? null : star)}
                aria-label={`Sleep quality ${star} of 5`}
                type="button"
              >
                <StarIcon />
              </button>
            ))}
          </div>
        </div>
        <div className="biometrics-actions">
          <button className="biometrics-save-btn" onClick={handleSave} disabled={saving} type="button">
            {saving ? 'Saving...' : 'Save'}
          </button>
          {saved && <span className="biometrics-saved-msg">Saved</span>}
        </div>
      </div>
    </div>
  );
};

export default DailyBiometrics;
